import { useCallback, useEffect, useMemo, useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Server, X, KeyRound, Star, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { api } from '../../lib/api';

const ADAPTER_TYPES = ['anthropic', 'openai_compatible'];
const BLANK = { name: '', adapter_type: 'openai_compatible', base_url: '', api_key: '' };

// MODEL popup (Ali, 2026-08-05, final): provider + model id + display name — connection
// lives with the provider. Both provider and id are editable; the id is the row's identity,
// so a rename creates the new row (carrying type/price/availability over) and deletes the old
// one. If the old id has usage history the audit guard keeps it, switched off, and says so.
function ModelDialog({ initial, providers, onClose, onSaved, onDeleteOld }) {
  const { t } = useTranslation();
  const isNew = !initial;
  const [f, setF] = useState(() => ({
    model_id: initial?.model_id || '',
    display_label: initial?.display_label || '',
    provider: initial?.provider || (providers[0]?.name ?? ''),
  }));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const newId = f.model_id.trim();
    if (!newId) { toast.error(t('modelsAdmin.idRequired')); return; }
    if (!f.provider) { toast.error(t('providersEditor.nameRequired')); return; }
    setBusy(true);
    try {
      // Full row travels: a rename must carry type/price/availability onto the new id.
      await onSaved({ ...(initial || {}), model_id: newId, provider: f.provider, display_label: f.display_label.trim() || null });
      if (!isNew && newId !== initial.model_id) {
        try {
          await onDeleteOld(initial.model_id);
        } catch (e) {
          if (e.message === 'model_has_usage') {
            await onSaved({ ...initial, user_selectable: false });
            toast.warning(t('modelsAdmin.renamedOldKept'));
          } else if (e.message === 'model_is_default') {
            toast.warning(t('modelsAdmin.errDefault'));
          } else if (e.message === 'model_is_qa') {
            toast.warning(t('modelsAdmin.errQa'));
          } else { throw e; }
        }
      }
      toast.success(t('modelsAdmin.saved'));
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="model-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? t('modelsAdmin.addTitle') : t('modelsAdmin.editTitle')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground"><T k="providersEditor.provider" /></span>
            <Select value={f.provider} onValueChange={(v) => setF({ ...f, provider: v })}>
              <SelectTrigger data-testid="model-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {providers.map((pr) => <SelectItem key={pr.name} value={pr.name}>{pr.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground"><T k="modelsAdmin.idLabel" /></span>
            <Input value={f.model_id} onChange={(e) => setF({ ...f, model_id: e.target.value })} className="font-data" data-testid="model-id-input" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground"><T k="modelsAdmin.labelLabel" /></span>
            <Input value={f.display_label} onChange={(e) => setF({ ...f, display_label: e.target.value })} data-testid="model-label-input" />
          </label>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="model-save"><T k="providersEditor.saveProvider" /></Button>
          <Button variant="ghost" onClick={onClose} data-testid="model-cancel"><T k="modelsAdmin.cancel" /></Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// PROVIDERS popup (Ali, 2026-08-05, final): every row has edit + delete; the form holds the
// SHARED connection — name (locked on edit: models point at it), adapter, URL, API key
// (write-only; blank keeps the stored one).
function ProvidersDialog({ providers, onClose, onChanged }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('providersEditor.nameRequired')); return; }
    setBusy(true);
    try {
      await api.upsertModelProvider({ name: form.name.trim(), adapter_type: form.adapter_type, base_url: form.base_url, api_key: form.api_key });
      toast.success(t('providersEditor.saved', { name: form.name.trim() }));
      setForm(null);
      await onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const del = async (pr) => {
    try {
      await api.tenantAdmin('delete_model_provider', { name: pr.name });
      toast.success(t('modelsAdmin.provDeleteDone'));
      await onChanged();
    } catch (e) {
      const key = { provider_has_models: 'errProviderHasModels', provider_is_default: 'errProviderDefault' }[e.message];
      toast.error(key ? t(`modelsAdmin.${key}`) : e.message);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="providers-dialog">
        <DialogHeader><DialogTitle><T k="modelsAdmin.providersTitle" /></DialogTitle></DialogHeader>
        <div className="flex flex-col gap-2">
          {providers.map((pr) => (
            <div key={pr.name} className="flex items-center gap-3 text-sm border-b border-border pb-2" data-testid={`prov-row-${pr.name}`}>
              <span className="font-medium text-foreground">{pr.name}</span>
              <span className="text-xs text-muted-foreground">{pr.adapter_type}</span>
              {pr.key_set
                ? <span className="inline-flex items-center gap-1 text-xs text-success"><KeyRound className="h-3 w-3" /> {t('providersEditor.keySet')}</span>
                : <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><KeyRound className="h-3 w-3" /> {t('providersEditor.keyNone')}</span>}
              <span className="ms-auto flex items-center gap-1">
                <Button variant="ghost" size="sm"
                  onClick={() => setForm({ name: pr.name, adapter_type: pr.adapter_type, base_url: pr.base_url || '', api_key: '', isNew: false, key_set: pr.key_set })}
                  data-testid={`prov-edit-${pr.name}`}>
                  {t('providersEditor.edit')}
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => del(pr)} data-testid={`prov-delete-${pr.name}`}>
                  {t('modelsAdmin.delete')}
                </Button>
              </span>
            </div>
          ))}
        </div>

        {form ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-border pt-3" data-testid="prov-form">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground"><T k="providersEditor.colName" /></span>
              <Input value={form.name} disabled={!form.isNew} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prov-name-input" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground"><T k="providersEditor.adapterType" /></span>
              <Select value={form.adapter_type} onValueChange={(v) => setForm({ ...form, adapter_type: v })}>
                <SelectTrigger data-testid="prov-adapter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ADAPTER_TYPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="flex flex-col gap-1 sm:col-span-2">
              <span className="text-xs text-muted-foreground"><T k="providersEditor.colBaseUrl" /></span>
              <Input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} data-testid="prov-baseurl-input" />
            </label>
            <label className="flex flex-col gap-1 sm:col-span-2">
              <span className="text-xs text-muted-foreground"><T k="providersEditor.apiKeyLabel" /></span>
              <Input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="sk-…" data-testid="prov-apikey-input" />
              {form.key_set && <span className="text-[11px] text-muted-foreground"><T k="modelsAdmin.keyKept" /></span>}
            </label>
            <div className="sm:col-span-2 flex gap-2">
              <Button size="sm" onClick={save} disabled={busy} data-testid="prov-save"><T k="providersEditor.saveProvider" /></Button>
              <Button size="sm" variant="ghost" onClick={() => setForm(null)} data-testid="prov-form-cancel"><T k="modelsAdmin.cancel" /></Button>
            </div>
          </div>
        ) : (
          <div>
            <Button size="sm" variant="outline" onClick={() => setForm({ name: '', adapter_type: 'openai_compatible', base_url: '', api_key: '', isNew: true, key_set: false })} data-testid="prov-add">
              <Plus className="h-3.5 w-3.5 me-1.5" /> {t('modelsAdmin.addProvider')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// PRICE popup — the row's money fact. Free IS the no-price state, so a free row's button reads
// "add price". Paid-without-cost is refused here AND server-side (paid_model_requires_cost).
// (Restored 2026-08-05 — the forms rewrite cut it while its call site stayed.)
function ModelPriceDialog({ row, onClose, onSaved }) {
  const { t } = useTranslation();
  const [paid, setPaid] = useState(row.charge_type !== 'flat');
  const [inM, setInM] = useState(row.usd_price_per_1k_input != null ? String(row.usd_price_per_1k_input * 1000) : '');
  const [outM, setOutM] = useState(row.usd_price_per_1k_output != null ? String(row.usd_price_per_1k_output * 1000) : '');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const ask = () => {
    if (paid && !Number(inM) && !Number(outM)) { toast.error(t('modelsAdmin.costRequired')); return; }
    setConfirming(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      await onSaved({
        ...row,
        charge_type: paid ? 'formula' : 'flat',
        flat_credits: null,
        usd_price_per_1k_input: paid && inM !== '' ? Number(inM) / 1000 : null,
        usd_price_per_1k_output: paid && outM !== '' ? Number(outM) / 1000 : null,
        markup: null,
      });
      toast.success(t('modelsAdmin.saved'));
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); setConfirming(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="model-price-dialog">
        <DialogHeader><DialogTitle><T k="modelsAdmin.priceTitle" values={{ model: row.display_label || row.model_id }} /></DialogTitle></DialogHeader>
        {!confirming ? (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground"><T k="modelsAdmin.typeLabel" /></span>
              <Select value={paid ? 'paid' : 'free'} onValueChange={(v) => setPaid(v === 'paid')}>
                <SelectTrigger data-testid="price-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid"><T k="modelsAdmin.typePaid" /></SelectItem>
                  <SelectItem value="free"><T k="modelsAdmin.typeFree" /></SelectItem>
                </SelectContent>
              </Select>
            </label>
            {paid && (
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground"><T k="modelsAdmin.costIn" /></span>
                  <Input type="number" step="0.01" value={inM} onChange={(e) => setInM(e.target.value)} data-testid="price-cost-in" />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground"><T k="modelsAdmin.costOut" /></span>
                  <Input type="number" step="0.01" value={outM} onChange={(e) => setOutM(e.target.value)} data-testid="price-cost-out" />
                </label>
                <p className="col-span-2 text-xs text-muted-foreground -mt-1"><T k="modelsAdmin.costHint" /></p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-foreground" data-testid="price-confirm-text">
            {paid
              ? t('modelsAdmin.confirmPaid', { in: Number(inM) || 0, out: Number(outM) || 0 })
              : t('modelsAdmin.confirmFree')}
          </p>
        )}
        <DialogFooter>
          {!confirming
            ? <Button onClick={ask} data-testid="price-continue"><T k="modelsAdmin.save" /></Button>
            : <Button onClick={save} disabled={busy} data-testid="price-confirm-save"><T k="modelsAdmin.save" /></Button>}
          <Button variant="ghost" onClick={onClose} data-testid="price-cancel"><T k="modelsAdmin.cancel" /></Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ProvidersEditor({ models = [], onSaveModel, onReloadModels }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);   // {initial} for the model+provider popup
  const [priceRow, setPriceRow] = useState(null); // the row whose price popup is open
  const [deleting, setDeleting] = useState(null); // the row whose delete confirmation is open
  const [providersOpen, setProvidersOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Hard-delete, server-guarded: the platform default, the QA model, and any model with usage
  // history are refused with a named reason — history stays auditable, deactivate-only.
  const doDelete = async () => {
    setDeleteBusy(true);
    try {
      await api.tenantAdmin('delete_model_price', { model_id: deleting.model_id });
      toast.success(t('modelsAdmin.deleteDone'));
      setDeleting(null);
      await onReloadModels?.();
    } catch (e) {
      const key = { model_is_default: 'errDefault', model_is_qa: 'errQa', model_has_usage: 'errReferenced' }[e.message];
      toast.error(key ? t(`modelsAdmin.${key}`) : e.message);
    } finally { setDeleteBusy(false); }
  };
  // Platform default (fallback identity — never a key).
  const [def, setDef] = useState({ provider: '', model_id: '', prompt_caching: false });
  const [savingDef, setSavingDef] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [provs, d] = await Promise.all([api.listModelProviders(), api.getPlatformDefault()]);
      setRows(provs);
      setDef({ provider: d.provider || '', model_id: d.model_id || '', prompt_caching: !!d.prompt_caching });
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // فعال on a MODEL row = offered to users (user_selectable, server-enforced on every call).
  // Platform-default and QA resolution deliberately ignore it, so a model can serve as the
  // default while staying out of the user picker.
  const toggleModel = async (m, v) => {
    try { await onSaveModel({ ...m, user_selectable: v }); }
    catch (e) { toast.error(e.message); }
  };

  // 068: what this model may be trusted with. Bespoke prototypes need capable/frontier; a model
  // nobody has assessed stays 'unknown' and is refused — honest ignorance, never a silent yes.
  const setTier = async (m, tier) => {
    try {
      await api.tenantAdmin('set_model_capability', { model_id: m.model_id, capability_tier: tier });
      toast.success(t('tenantAdmin.saved'));
      await onReloadModels?.();
    } catch (e) { toast.error(e.message); }
  };

  // Saving from the model+provider popup: whole-row write, then reload both sides.
  const saveModelProvider = async (patch) => {
    const existing = models.find((m) => m.model_id === patch.model_id);
    await onSaveModel({
      charge_type: 'flat', flat_credits: null, usd_price_per_1k_input: null,
      usd_price_per_1k_output: null, markup: null, user_selectable: true,
      tier_hint: null, is_active: true,
      ...(existing || {}),
      ...patch,
    });
    await load();
    await onReloadModels?.();
  };

  // Models offered for the chosen default provider (fall back to any, so the current default is selectable).
  const defaultModels = useMemo(() => {
    const forProvider = models.filter((m) => m.provider === def.provider).map((m) => m.model_id);
    if (def.model_id && !forProvider.includes(def.model_id)) forProvider.unshift(def.model_id);
    return forProvider;
  }, [models, def.provider, def.model_id]);

  const saveDefault = async () => {
    if (!def.provider || !def.model_id) { toast.error(t('providersEditor.pickBoth')); return; }
    setSavingDef(true);
    try {
      await api.setPlatformDefault(def);
      toast.success(t('providersEditor.defaultSaved'));
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setSavingDef(false); }
  };

  const defaultRowMissingKey = rows.some((r) => r.name === def.provider && !r.key_set);
  // The last net for the silent-free-model trap: the platform default serving every un-picked
  // round while charging as a free model is a fact the admin must SEE, not discover in revenue.
  const defModelRow = models.find((m) => m.model_id === def.model_id);
  const defaultChargesFree = !!def.model_id && (!defModelRow || defModelRow.charge_type === 'flat');

  return (
    <div className="border border-border rounded-lg p-5 flex flex-col gap-5" data-testid="providers-editor">
      <div className="flex items-center gap-2">
        <Server className="h-5 w-5 text-foreground" />
        <h3 className="font-heading font-semibold text-foreground"><T k="providersEditor.title" /></h3>
      </div>
      <p className="text-sm text-muted-foreground -mt-3">
        {t('providersEditor.intro')}
      </p>

      {/* One row per MODEL. A provider serves many models on one key, so provider columns
          repeat across its rows — that is the join, not duplication. */}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={() => setProvidersOpen(true)} data-testid="open-providers">
          <Server className="h-4 w-4 me-2" /> {t('modelsAdmin.providersBtn')}
        </Button>
        <Button size="sm" onClick={() => setDialog({ initial: null })} data-testid="add-model-provider">
          <Plus className="h-4 w-4 me-2" /> {t('modelsAdmin.addTitle')}
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="providers-table">
          <thead>
            <tr className="text-start text-muted-foreground border-b border-border">
              <th className="text-start py-2 pe-4"><T k="modelsAdmin.colModel" /></th>
              <th className="text-start py-2 pe-4"><T k="providersEditor.colName" /></th>
              <th className="text-start py-2 pe-4"><T k="providersEditor.colKey" /></th>
              <th className="text-start py-2 pe-4"><T k="providersEditor.colActive" /></th>
              <th className="text-start py-2 pe-4"><T k="modelsAdmin.colCapability" /></th>
              <th className="text-start py-2 pe-4"><T k="modelsAdmin.colPrice" /></th>
              <th className="text-start py-2" />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="py-3 text-muted-foreground"><T k="common.loading" /></td></tr>}
            {!loading && models.length === 0 && (
              <tr><td colSpan={6} className="py-3 text-muted-foreground" data-testid="providers-empty"><T k="modelsAdmin.empty" /></td></tr>
            )}
            {models.map((m) => {
              const pr = rows.find((r) => r.name === m.provider);
              const isDefault = m.model_id === def.model_id;
              const paid = m.charge_type !== 'flat';
              return (
                <tr key={m.model_id} className="border-b border-border align-top" data-testid={`model-row-${m.model_id}`}>
                  <td className="py-2 pe-4">
                    <div className="font-medium text-foreground font-data">{m.display_label || m.model_id}</div>
                    {isDefault && (
                      <span className="mt-1 inline-flex items-center gap-1 text-xs text-primary" data-testid={`model-default-${m.model_id}`}>
                        <Star className="h-3 w-3" /> {t('modelsAdmin.isDefault')}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pe-4 text-muted-foreground">
                    {m.provider || '—'}
                    {pr && pr.active === false && (
                      <div className="text-xs text-warning" data-testid={`provider-off-${m.model_id}`}><T k="modelsAdmin.providerOff" /></div>
                    )}
                  </td>
                  <td className="py-2 pe-4">
                    {pr?.key_set
                      ? <span className="inline-flex items-center gap-1 text-success"><KeyRound className="h-3.5 w-3.5" /> {t('providersEditor.keySet')}</span>
                      : <span className="inline-flex items-center gap-1 text-muted-foreground"><KeyRound className="h-3.5 w-3.5" /> {t('providersEditor.keyNone')}</span>}
                  </td>
                  <td className="py-2 pe-4">
                    <Switch checked={!!m.user_selectable} onCheckedChange={(v) => toggleModel(m, v)} data-testid={`model-active-${m.model_id}`} />
                  </td>
                  <td className="py-2 pe-4">
                    <select
                      className="text-xs border border-border rounded-md px-2 py-1 bg-card text-foreground"
                      value={m.capability_tier || 'unknown'}
                      onChange={(e) => setTier(m, e.target.value)}
                      data-testid={`model-capability-${m.model_id}`}
                      title={t('modelsAdmin.capabilityHelp')}
                    >
                      <option value="unknown">{t('prototype.tierUnknown')}</option>
                      <option value="basic">{t('prototype.tierBasic')}</option>
                      <option value="capable">{t('prototype.tierCapable')}</option>
                      <option value="frontier">{t('prototype.tierFrontier')}</option>
                    </select>
                  </td>
                  <td className="py-2 pe-4">
                    <Button variant="outline" size="sm" onClick={() => setPriceRow(m)} data-testid={`model-price-${m.model_id}`}>
                      {paid ? t('modelsAdmin.editPrice') : t('modelsAdmin.addPrice')}
                    </Button>
                    <div className="text-xs text-muted-foreground mt-1">
                      {paid
                        ? t('modelsAdmin.paidBadge', { in: (m.usd_price_per_1k_input ?? 0) * 1000, out: (m.usd_price_per_1k_output ?? 0) * 1000 })
                        : t('modelsAdmin.freeBadge')}
                    </div>
                  </td>
                  <td className="py-2 text-end whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => setDialog({ initial: m })} data-testid={`model-edit-${m.model_id}`}>
                      {t('providersEditor.edit')}
                    </Button>
                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDeleting(m)} data-testid={`model-delete-${m.model_id}`}>
                      {t('modelsAdmin.delete')}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {dialog && (
        <ModelDialog
          initial={dialog.initial}
          providers={rows}
          onClose={() => setDialog(null)}
          onSaved={saveModelProvider}
          onDeleteOld={(model_id) => api.tenantAdmin('delete_model_price', { model_id })}
        />
      )}
      {providersOpen && (
        <ProvidersDialog
          providers={rows}
          onClose={() => setProvidersOpen(false)}
          onChanged={async () => { await load(); await onReloadModels?.(); }}
        />
      )}
      {priceRow && (
        <ModelPriceDialog
          row={priceRow}
          onClose={() => setPriceRow(null)}
          onSaved={async (patch) => { await onSaveModel(patch); await onReloadModels?.(); }}
        />
      )}
      {deleting && (
        <Dialog open onOpenChange={(o) => { if (!o) setDeleting(null); }}>
          <DialogContent data-testid="model-delete-dialog">
            <DialogHeader><DialogTitle><T k="modelsAdmin.deleteTitle" /></DialogTitle></DialogHeader>
            <p className="text-sm text-foreground">
              {t('modelsAdmin.deleteConfirm', { model: deleting.display_label || deleting.model_id })}
            </p>
            <DialogFooter>
              <Button variant="destructive" onClick={doDelete} disabled={deleteBusy} data-testid="model-delete-confirm">
                {t('modelsAdmin.delete')}
              </Button>
              <Button variant="ghost" onClick={() => setDeleting(null)} data-testid="model-delete-cancel"><T k="modelsAdmin.cancel" /></Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Platform default model — separate control, never touches a key */}
      <div className="rounded-md bg-muted border border-border p-4 flex flex-col gap-3" data-testid="platform-default">
        <div className="flex items-center gap-2">
          <Star className="h-4 w-4 text-primary" />
          <span className="font-medium text-foreground text-sm"><T k="providersEditor.defaultTitle" /></span>
        </div>
        <p className="text-xs text-muted-foreground -mt-1"><T k="providersEditor.defaultHint" /></p>
        {defaultRowMissingKey && (
          <div className="inline-flex items-center gap-1 text-xs text-warning" data-testid="default-nokey-warning">
            <AlertTriangle className="h-3.5 w-3.5" /> {t('providersEditor.defaultMissingKey')}
          </div>
        )}
        {defaultChargesFree && (
          <div className="inline-flex items-center gap-1 text-xs text-warning" data-testid="default-free-warning">
            <AlertTriangle className="h-3.5 w-3.5" /> {defModelRow ? t('modelsAdmin.defaultFree') : t('modelsAdmin.defaultMissing')}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground"><T k="providersEditor.provider" /></span>
            <Select value={def.provider} onValueChange={(v) => setDef({ ...def, provider: v, model_id: '' })}>
              <SelectTrigger data-testid="default-provider-select"><SelectValue placeholder={t('providersEditor.selectProvider')} /></SelectTrigger>
              <SelectContent>
                {rows.map((r) => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground"><T k="providersEditor.model" /></span>
            <Select value={def.model_id} onValueChange={(v) => setDef({ ...def, model_id: v })} disabled={!def.provider}>
              <SelectTrigger data-testid="default-model-select"><SelectValue placeholder={t('providersEditor.selectModel')} /></SelectTrigger>
              <SelectContent>
                {defaultModels.length === 0 && <SelectItem value="__none" disabled><T k="providersEditor.noModels" /></SelectItem>}
                {defaultModels.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground"><T k="providersEditor.promptCaching" /></span>
            <div className="h-9 flex items-center">
              <Switch checked={def.prompt_caching} onCheckedChange={(v) => setDef({ ...def, prompt_caching: v })} data-testid="default-caching" />
            </div>
          </label>
        </div>
        <div>
          <Button onClick={saveDefault} disabled={savingDef} data-testid="default-save-button">
            {savingDef ? t('providersEditor.saving') : t('providersEditor.saveDefault')}
          </Button>
        </div>
      </div>
    </div>
  );
}
